import { Avatar, Box, Button, Card, CardActions, CardContent, CardHeader, CircularProgress, Container, Fab, Grid, IconButton, Slide, Snackbar, SvgIcon, Theme, Typography, makeStyles } from "@material-ui/core";
import { cyan, green, red, yellow } from "@material-ui/core/colors";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import { Alert } from "@material-ui/lab";
import { Result } from '@zxing/library';
import React, { FunctionComponent, useEffect, useState } from "react";
import { Link as RouterLink, useHistory, useRouteMatch } from 'react-router-dom';
import * as Tone from "tone";
import { AddedInventoryPackage, ListInventoryItem, useAddInventoryPackageMutation, useCheckPermissionQuery, useGetInventoryQuery, useInventoryExecutedMutation } from "../../graphql/generated/graphql";
import BarcodeDialog from "../../shared/BarcodeDialog";
import BarcodeKeyboard from "../../shared/BarcodeKeyboard";
import { useConfirmDialog } from "../../shared/ConfirmDialog";
import PermissionKeys from "../../shared/PermissionKeys";
import Beep from "./Beep";
import Counter from "./Counter";
import InventoryItem from "./InventoryItem";

const useStyles = makeStyles((theme: Theme) => ({
  layoutRoot: {
    marginTop: theme.spacing(2),
  },
  root: {
    paddingLeft: theme.spacing(1),
  },
  card: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  inventoryAvatar: {
    color: theme.palette.action.active,
  },
  inventoryAvatarExecutable: {
    backgroundColor: yellow[600],
    color: theme.palette.getContrastText(yellow[600]),
  },
  inventoryAvatarExecuted: {
    backgroundColor: green[600],
    color: theme.palette.getContrastText(green[600]),
  },
  errorAvatar: {
    backgroundColor: red[500],
  },
  item: {
    marginRight: theme.spacing(1),
  },
  filters: {
    marginLeft: "auto",
    marginRight: "auto",
    paddingRight: theme.spacing(1),
    width: 48 * 2,
    marginBottom: theme.spacing(2),
  },
  progress: {
    marginLeft: "auto",
    marginRight: "auto",
    width: 40,
  },
  startScan: {
    backgroundColor: cyan.A400,
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  complete: {
    backgroundColor: green.A400,
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  completed: {
    color: theme.palette.getContrastText(green[600]),
    backgroundColor: green[600],
  },
  rightAlignItem: {
    marginLeft: "auto",
  },
}));

let prevBarcode = "";

export default function Component()
{
  const noPermission = () =>
  {
    return <Container maxWidth="xs">
      <Card>
        <CardHeader title="Leltár" subheader="Nincs jogosultság" avatar={<Avatar className={classes.errorAvatar}>X</Avatar>} />
        <CardContent>
          <Typography variant="body2" component="p">Nincs jogosultságod az oldal megtekintéséhez.</Typography>
        </CardContent>
        <CardActions disableSpacing>
          <IconButton component={RouterLink} to="/" title="Vissza"><ArrowBackIcon /></IconButton>
        </CardActions>
      </Card>
    </Container>;
  }

  const maxItemCount = 30;

  const classes = useStyles();
  const { data: permissionData, loading: permissionLoading } = useCheckPermissionQuery({ variables: { PermissionKey: PermissionKeys.Warehouse.Inventory.Write } });
  const match = useRouteMatch<{ inventoryId: string }>("/inventory/:inventoryId");
  const { data, loading, error } = useGetInventoryQuery({
    skip: !permissionData?.CheckPermission,
    variables: {
      InventoryId: Number.parseInt(match?.params.inventoryId ?? ""),
      MaxItemCount: maxItemCount,
    }
  });
  const [getInventoryResult, setGetInventoryResult] = useState(data?.GetInventory);

  useEffect(() =>
  {
    if(data && data.GetInventory)
    {
      let newGetInventoryResult = {
        Inventory: { ...data.GetInventory.Inventory! },
        Items: [...data.GetInventory.Items!],
        PackageCount: data.GetInventory.PackageCount,
      };
      setGetInventoryResult(newGetInventoryResult);
    }
  }, [data]);


  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);

  const [addInventoryPackage, addInventoryPackageMutation] = useAddInventoryPackageMutation();
  const [alertError, setAlertError] = useState<string | null>(null);
  const [inventoryExecuted, inventoryExecutedMutation] = useInventoryExecutedMutation()
  const history = useHistory();

  const handleConfirm = async () =>
  {
    closeConfirmDialog();
    const { data } = await inventoryExecuted({ variables: { InventoryId: getInventoryResult?.Inventory?.InventoryId ?? 0 } });
    inventoryExecutedMutation.client.clearStore();
    if(data?.InventoryExecuted)
    {
      history.goBack();
    }
    else
    {
      setAlertError("A leltár lezárása sikertelen volt.");
    }
  };

  const { ConfirmDialog, openConfirmDialog, closeConfirmDialog } = useConfirmDialog(
    "Végrehajtva",
    "Biztos, hogy lezárod a leltárt?",
    handleConfirm
  );

  if(loading || permissionLoading || !getInventoryResult) return <Box className={classes.progress}><CircularProgress color="secondary" /></Box>;
  if(error) return <Typography>{error.message}</Typography>;
  if(permissionData && !permissionData.CheckPermission) return noPermission();

  const executedButtonClicked = async () =>
  {
    openConfirmDialog();
  };

  const Inventory: FunctionComponent<{ Item: ListInventoryItem, PackageCount: number }> = (props) =>
  {

    function getAvatarClass(iventoryStateId?: number)
    {
      switch(iventoryStateId)
      {
        case 1: return classes.inventoryAvatarExecutable;
        case 2: return classes.inventoryAvatarExecuted;
        default: return classes.inventoryAvatar
      }
    }

    function UTCToLocal(date: string): string
    {
      if(!date) return "";
      let d = new Date(date);
      return d.toLocaleString('hu-HU', { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
    }

    return <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card className={classes.card}>
        <CardHeader title={`#${props.Item.InventoryId}`}
          subheader={`Leltár: ${props.Item.InventoryState?.Name}`}
          avatar={<Avatar className={getAvatarClass(props.Item.InventoryState?.InventoryStateId ?? undefined)} ><AssignmentOutlinedIcon /></Avatar>}
        />
        <CardContent>
          <Grid container>
            <Grid item className={classes.item}><Typography>{UTCToLocal(props.Item.BeginAt)}</Typography></Grid>
            {props.Item.EndAt ? <Grid item className={classes.item}><Typography>{UTCToLocal(props.Item.EndAt)}: </Typography></Grid> : <></>}
          </Grid>
        </CardContent>
        {props.Item.InventoryState?.InventoryStateId === 1 ? <CardActions disableSpacing>
          <Counter className={classes.completed} count={props.PackageCount} limit={0} />
          <Button variant="contained" disabled={props.PackageCount === 0} className={classes.rightAlignItem} startIcon={<SaveOutlinedIcon />} onClick={executedButtonClicked}>Végrehajtva</Button>
        </CardActions>
          : <></>}
      </Card>
    </Grid>;
  }

  function onBarcodeUpdate(err: unknown, result: Result | undefined)
  {
    if(result)
    {
      const barcode = result.getText();
      if(prevBarcode !== barcode)
      {
        prevBarcode = barcode;
        onBarcode(barcode)
      }
    }
  }

  async function onBarcode(barcode: string)
  {
    if(!getInventoryResult)
    {
      Beep(440, "sine");
      return;
    }
    setBarcodeDialogOpen(false);
    const addedInventoryPackage: AddedInventoryPackage = {
      InventoryId: getInventoryResult.Inventory.InventoryId,
      PackageNumber: barcode,
    };
    const { data } = await addInventoryPackage({ variables: { AddedInventoryPackage: addedInventoryPackage } });
    if(data?.AddInventoryPackage.Error)
    {
      setAlertError(data?.AddInventoryPackage.Error);
      Beep(440, "sine");
      return;
    }
    const item = data?.AddInventoryPackage.Item;
    if(item)
    {
      getInventoryResult.Items.unshift(item);
      while(getInventoryResult.Items.length > maxItemCount) getInventoryResult.Items.pop();
      let newGetInventoryResult = {
        Inventory: { ...getInventoryResult.Inventory! },
        Items: [...getInventoryResult.Items!],
        PackageCount: data.AddInventoryPackage.PackageCount ?? 0,
      };
      setGetInventoryResult(newGetInventoryResult);
      await Beep(880, "square");
      addInventoryPackageMutation.client.clearStore();
      return;
    }
    Beep(440, "sine");
  }

  return <Box className={classes.layoutRoot}>
    <Grid container className={classes.root}>
      {getInventoryResult?.Inventory ? <Inventory Item={getInventoryResult?.Inventory} PackageCount={getInventoryResult.PackageCount} /> : <></>}
      {getInventoryResult?.Items.map(i => <InventoryItem
        key={i.InventoryPackageId}
        Item={i}
      />)}
    </Grid>
    <BarcodeKeyboard enabled={!barcodeDialogOpen} onBarcodeUpdate={onBarcode} />
    <BarcodeDialog dialogOpen={barcodeDialogOpen} onBarcodeUpdate={onBarcodeUpdate} onCancel={() => { setBarcodeDialogOpen(false) }} />
    <ConfirmDialog />
    <Fab className={classes.startScan} onClick={() =>
    {
      Tone.context.resume().then(() =>
      {
        prevBarcode = "";
        setBarcodeDialogOpen(true);
        Tone.context.resume();
      });
    }} >
      <SvgIcon>
        <path stroke="null" id="svg_3" d="m1.046054,4.697369c0,-0.576125 0.467108,-1.043233 1.043233,-1.043233l2.608082,0l0,-1.043233l-2.608082,0c-1.152251,0 -2.086466,0.934215 -2.086466,2.086466l0,2.608082l1.043233,0l0,-2.608082zm0,14.605261l0,-2.608082l-1.043233,0l0,2.608082c0,1.152251 0.934215,2.086466 2.086466,2.086466l2.608082,0l0,-1.043233l-2.608082,0c-0.576125,0 -1.043233,-0.467108 -1.043233,-1.043233zm21.907892,0c0,0.576125 -0.467108,1.043233 -1.043233,1.043233l-2.608082,0l0,1.043233l2.608082,0c1.152251,0 2.086466,-0.934215 2.086466,-2.086466l0,-2.608082l-1.043233,0l0,2.608082zm-1.043233,-16.691727l-2.608082,0l0,1.043233l2.608082,0c0.576125,0 1.043233,0.467108 1.043233,1.043233l0,2.608082l1.043233,0l0,-2.608082c0,-1.152251 -0.934215,-2.086466 -2.086466,-2.086466zm-20.864659,9.389096c0,0.287932 0.233684,0.521616 0.521616,0.521616l20.864659,0c0.287932,0 0.521616,-0.233684 0.521616,-0.521616s-0.233684,-0.521616 -0.521616,-0.521616l-20.864659,0c-0.287932,0 -0.521616,0.233684 -0.521616,0.521616zm8.345864,-6.259398l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm-6.259398,0l0,4.694548l1.043233,0l0,-4.694548l-1.043233,0zm8.345864,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm5.216165,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm2.086466,0l-1.043233,0l0,4.694548l1.043233,0l0,-4.694548zm1.043233,0l0,4.694548l1.043233,0l0,-4.694548l-1.043233,0zm-12.518795,0l-2.086466,0l0,4.694548l2.086466,0l0,-4.694548zm7.302631,0l-2.086466,0l0,4.694548l2.086466,0l0,-4.694548zm-6.259398,12.518795l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm-4.172932,0l0,-4.694548l-1.043233,0l0,4.694548l1.043233,0zm6.259398,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm5.216165,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm2.086466,0l1.043233,0l0,-4.694548l-1.043233,0l0,4.694548zm3.129699,0l0,-4.694548l-1.043233,0l0,4.694548l1.043233,0zm-15.648494,0l2.086466,0l0,-4.694548l-2.086466,0l0,4.694548zm7.302631,0l2.086466,0l0,-4.694548l-2.086466,0l0,4.694548z" clip-rule="evenodd" fill-rule="evenodd" />
      </SvgIcon>
    </Fab>
    {alertError ?
      <Snackbar onFocus={e => { e.currentTarget.blur() }} open={!!alertError} autoHideDuration={10000} onClose={() => setAlertError(null)} TransitionComponent={props => <Slide direction="up" {...props} />}>
        <Alert onClose={() => setAlertError(null)} severity="error">{alertError}</Alert>
      </Snackbar>
      : <> </>
    }
  </Box>;
}
